import React from 'react'
import { graphql, HeadProps, Link, PageProps } from 'gatsby'
import { Box, Button } from '@mui/material'
import { Layout } from '../components/Layout'
import { Header } from '../containers/Header'
import { Footer } from '../containers/Footer'
import { CommonHead } from '../components/CommonHead'
import { Section } from '../components/Section'
import { SectionContentMarketCityHeader } from '../components/SectionContentMarketCityHeader'
import { SectionContentMetrics } from '../components/SectionContentMetrics'
import { SectionContentCardList } from '../components/SectionContentCardList'
import { SectionContentTestimonials } from '../components/SectionContentTestimonials'
import { SectionContentImageWithText } from '../components/SectionContentImageWithText'
import { SectionContentMarketGigsWrapper } from '../components/SectionContentMarketGigsWrapper'
import { MarketGigsListContainerLazy } from '../containers/MarketGigsList/lazy'
import {
  MARKET_METRICS,
  MARKET_SKILL_CARDS,
  MARKET_TESTIMONIALS,
} from './marketSharedContent'

export interface MarketCityContext {
  id: number
  city: string
  state: string
  slug: string
  stateSlug: string
  marketPath: string
  heroImagePublicId?: string
  heroAlt?: string
  description?: string
  isCanonical: boolean
  canonicalPath: string
}

interface MarketCityData {
  site: { siteMetadata: { siteUrl: string } }
}

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<MarketCityData, MarketCityContext>) => {
  const { city, canonicalPath, isCanonical } = pageContext
  const siteUrl = data.site.siteMetadata.siteUrl
  return (
    <CommonHead
      title={`Qwick in ${city} | On-demand hospitality staffing`}
      description={`See how Qwick connects ${city} hospitality businesses with experienced gig workers, and how pros pick up shifts that fit their schedule.`}
      url={siteUrl + location.pathname}
    >
      {!isCanonical && <link rel="canonical" href={siteUrl + canonicalPath} />}
    </CommonHead>
  )
}

const Page = ({
  pageContext,
}: PageProps<MarketCityData, MarketCityContext>) => {
  const {
    id,
    city,
    state,
    marketPath,
    heroImagePublicId,
    heroAlt,
    description,
  } = pageContext
  return (
    <Layout header={<Header />}>
      <Section backgroundColor="icons" showBottomWaves>
        <SectionContentMarketCityHeader title={city} state={state} />
      </Section>
      {description && heroImagePublicId && (
        <Section backgroundColor="white" padding={100}>
          <SectionContentImageWithText
            subheading={`${city}, ${state}`}
            heading={`What Qwick is Doing in ${city}`}
            description={{ description }}
            imagePlacement="left"
            image={{ publicId: heroImagePublicId, alt: heroAlt ?? '' }}
          />
        </Section>
      )}
      <Section backgroundColor="light-gray" padding={100}>
        <SectionContentMetrics {...MARKET_METRICS} />
      </Section>
      <Section backgroundColor="white" padding={100}>
        <SectionContentCardList {...MARKET_SKILL_CARDS} />
      </Section>
      <Section backgroundColor="light-gray" padding={50} slug="testimonials">
        <SectionContentTestimonials {...MARKET_TESTIMONIALS} />
      </Section>
      <Section backgroundColor="white" padding={100}>
        <SectionContentMarketGigsWrapper marketCity={city}>
          <MarketGigsListContainerLazy
            marketId={id}
            limit={8}
            fade={false}
            linkPrefix={`${marketPath}/job`}
            afterListContent={
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  component={Link}
                  to={`${marketPath}/jobs`}
                  variant="contained"
                >
                  View all open gigs in {city}
                </Button>
              </Box>
            }
          />
        </SectionContentMarketGigsWrapper>
      </Section>
      <Footer />
    </Layout>
  )
}

export const query = graphql`
  query MarketCityTemplate {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`

export default Page
